import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _typeof from '@babel/runtime/helpers/typeof';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import classNames from 'classnames';
import isEmpty from 'lodash-es/isEmpty';
import PropTypes from 'prop-types';
import React, { useState, useMemo, useCallback, useRef } from 'react';
import { SketchPicker } from 'react-color';
import { FormattedMessage } from 'react-intl';
import tinycolor from 'tinycolor2';
import { useDocumentEvent } from '@panneau/core/hooks';

var styles = {"container":"panneau-field-color-container","disabled":"panneau-field-color-disabled","button":"panneau-field-color-button","emptyLabel":"panneau-field-color-emptyLabel"};

var propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
    color: PropTypes.string,
    alpha: PropTypes.number
  })]),
  defaultValue: PropTypes.string,
  "native": PropTypes.bool,
  withAlpha: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  onChange: PropTypes.func
};
var defaultProps = {
  value: null,
  defaultValue: '#000000',
  // Only for react select
  "native": false,
  withAlpha: false,
  disabled: false,
  className: null,
  onChange: null
};
var ColorPickerField = function ColorPickerField(_ref) {
  var value = _ref.value,
    defaultValue = _ref.defaultValue,
    _native = _ref["native"],
    withAlpha = _ref.withAlpha,
    disabled = _ref.disabled,
    className = _ref.className,
    onChange = _ref.onChange;
  var emptyValue = isEmpty(value);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    pickerOpened = _useState2[0],
    setPickerOpened = _useState2[1];
  var rgbaColor = useMemo(function () {
    if (!emptyValue) {
      var newColor = tinycolor(_typeof(value) === 'object' ? value.color : value);
      newColor.setAlpha(_typeof(value) === 'object' ? value.alpha : 1);
      return newColor;
    }
    return null;
  }, [emptyValue, value]);
  var hexColor = useMemo(function () {
    return rgbaColor !== null ? rgbaColor.toHexString() : defaultValue;
  }, [rgbaColor, defaultValue]);
  var alphaValue = useMemo(function () {
    return rgbaColor !== null ? rgbaColor.getAlpha() || null : null;
  }, [rgbaColor, defaultValue]);
  var finalColor = withAlpha && alphaValue !== null && alphaValue !== 1 ? "".concat(hexColor, " (").concat(alphaValue.toFixed(2), ")") : hexColor;
  var pickerStyle = useMemo(function () {
    return {
      picker: {
        boxShadow: 'none',
        backgroundColor: '#FFF',
        borderRadius: '4px'
      },
      label: {
        color: '#FFF'
      }
    };
  }, []);
  var onInputChange = useCallback(function (e) {
    if (onChange !== null) {
      onChange({
        color: e.target.value,
        alpha: 1
      });
    }
  }, [onChange]);
  var onInputClick = useCallback(function (e) {
    if (!_native) {
      e.preventDefault();
      setPickerOpened(!pickerOpened);
    }
  }, [_native, setPickerOpened, pickerOpened]);
  var onPickerChange = useCallback(function (newValue) {
    if (onChange !== null) {
      var _ref2 = newValue || {},
        hex = _ref2.hex,
        _ref2$rgb$a = _ref2.rgb.a,
        alpha = _ref2$rgb$a === void 0 ? 1 : _ref2$rgb$a;
      onChange(withAlpha ? {
        color: hex,
        alpha: alpha
      } : hex);
    }
  }, [onChange, withAlpha]);
  var pickerRef = useRef(null);
  var onDocumentClick = useCallback(function (e) {
    if (pickerRef.current !== null && !pickerRef.current.contains(e.target)) {
      setPickerOpened(false);
    }
  }, [setPickerOpened]);
  useDocumentEvent('click', onDocumentClick, !_native && pickerOpened);
  var finalLabel = emptyValue ? /*#__PURE__*/React.createElement("span", {
    className: styles.emptyLabel
  }, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "6cSFtU",
    defaultMessage: [{
      "type": 0,
      "value": "No color"
    }]
  })) : finalColor;
  var finalHexColor = emptyValue ? defaultValue : hexColor;
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, 'position-relative', _defineProperty(_defineProperty({}, styles.disabled, disabled), className, className !== null)]),
    ref: pickerRef
  }, /*#__PURE__*/React.createElement("label", {
    className: "input-group"
  }, /*#__PURE__*/React.createElement("input", {
    type: "color",
    className: classNames(['flex-grow-0', 'form-control', 'form-control-color']),
    style: {
      width: 40
    },
    value: finalHexColor,
    disabled: disabled,
    onChange: onInputChange,
    onClick: onInputClick
  }), !_native ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: classNames([styles.button, 'flex-grow-1', 'text-uppercase', 'input-group-text']),
    disabled: disabled,
    style: {
      minWidth: 100,
      cursor: 'pointer'
    },
    onClick: onInputClick
  }, finalLabel) : /*#__PURE__*/React.createElement("span", {
    className: classNames(['flex-grow-1', 'text-uppercase', 'input-group-text']),
    disabled: disabled,
    style: {
      minWidth: 100,
      cursor: 'pointer'
    }
  }, finalLabel)), !_native ? /*#__PURE__*/React.createElement("div", {
    className: classNames(['position-absolute', 'mt-1', 'p-2', 'border', 'bg-white', {
      invisible: !pickerOpened
    }]),
    style: {
      zIndex: 4,
      width: 300
    }
  }, /*#__PURE__*/React.createElement(SketchPicker, {
    disableAlpha: !withAlpha,
    color: rgbaColor || defaultValue,
    styles: pickerStyle,
    onChange: onPickerChange,
    disabled: disabled
  })) : null);
};
ColorPickerField.propTypes = propTypes;
ColorPickerField.defaultProps = defaultProps;

var definition = {
  id: 'color',
  component: 'Color'
};

export { ColorPickerField as default, definition };
